import React, { useState, useCallback } from 'react';
import {
    IndexTable,
    LegacyCard,
    IndexFilters,
    useSetIndexFiltersMode,
    useIndexResourceState,
    Text,
    Badge,
    useBreakpoints,
    ButtonGroup
} from '@shopify/polaris';
import { useDispatch } from 'react-redux';
import { deletePriceUpdateLogAsync } from '../../redux/priceUpdateLog';
import ModalComponent from '../common/Modal';
import { SORT_OPTIONS, extractDate, dynamicResourceName, PRICE_UPDATE_LOG_FILTERS, PRICE_UPDATE_LOG_HEADINGS } from '../../utils';
import ViewRuleField from '../rule/ViewRuleField';
import ViewRateField from './ViewRateField';

const PriceUpdateLogTable = ({
    priceUpdateLogData,
    pagination,
    setCurrentPage,
    currentPage,
    searchValue,
    handleSearchValueChange,
    handleSearchValueRemove,
    loading,
    sortSelected,
    setSortSelected,
    selectedFilter,
    setSelectedFilter,
    isDeleteLoading,
    handleCallBack
}) => {
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const tabs = PRICE_UPDATE_LOG_FILTERS.map((item, index) => ({
        content: item,
        index,
        onAction: () => { },
        id: `${item}-${index}`,
        isLocked: index === 0
    }));

    const { mode, setMode } = useSetIndexFiltersMode();

    const priceUpdateLogs = priceUpdateLogData || [];

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(priceUpdateLogs);

    const handleNext = () => {
        if (pagination.currentPage < pagination.totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrevious = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleModalOpen = useCallback(() => setIsModalOpen(!isModalOpen), [isModalOpen]);

    const handleDelete = () => {
        dispatch(deletePriceUpdateLogAsync({
            payload: {
                ids: selectedResources
            },
            callback: (success, message) => {
                if (success) {
                    setIsModalOpen(false);
                    clearSelection();
                    handleCallBack(success, message);
                }
            }
        }));
    };

    const promotedBulkActions = [
        {
            destructive: true,
            content: selectedResources.length > 1 ? 'Delete logs' : 'Delete log',
            onAction: () => handleModalOpen()
        },
    ];

    const rowMarkup = priceUpdateLogs.map(
        (
            { _id, updatedBy, updateType, status, intervalDate, updateInterval, ruleId, priceSource, rates },
            index,
        ) => (
            <IndexTable.Row
                id={_id}
                key={_id}
                selected={selectedResources.includes(_id)}
                position={index}
            >
                <IndexTable.Cell>
                    <Text variant="bodyMd" fontWeight="bold" as="span">
                        {updateType}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{extractDate(intervalDate)}</IndexTable.Cell>
                <IndexTable.Cell>
                    <Badge tone={status === 'Successful' ? 'success' : status === 'Failed' ? 'critical' : 'info'}>
                        {status}
                    </Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>{updatedBy}</IndexTable.Cell>
                <IndexTable.Cell>{updateInterval?.value} {updateInterval?.unit}</IndexTable.Cell>
                <IndexTable.Cell>
                    <ButtonGroup gap='tight'>
                        {priceSource === 'api' && <ViewRateField rates={rates} date={intervalDate} />}
                        <ViewRuleField rule={ruleId} />
                    </ButtonGroup>
                </IndexTable.Cell>
            </IndexTable.Row>
        ),
    );

    return (
        <>
            <LegacyCard>
                <IndexFilters
                    sortOptions={SORT_OPTIONS}
                    sortSelected={sortSelected}
                    queryValue={searchValue}
                    queryPlaceholder="Searching in all"
                    onQueryChange={handleSearchValueChange}
                    onQueryClear={handleSearchValueRemove}
                    onSort={setSortSelected}
                    cancelAction={{
                        onAction: handleSearchValueRemove,
                        disabled: false,
                        loading: false,
                    }}
                    selected={selectedFilter}
                    onSelect={setSelectedFilter}
                    tabs={tabs}
                    canCreateNewView={false}
                    filters={[]}
                    appliedFilters={[]}
                    onClearAll={handleSearchValueRemove}
                    mode={mode}
                    setMode={setMode}
                />
                <IndexTable
                    condensed={useBreakpoints().smDown}
                    resourceName={dynamicResourceName('priceUpdateLog')}
                    itemCount={priceUpdateLogs.length}
                    selectedItemsCount={
                        allResourcesSelected ? 'All' : selectedResources.length
                    }
                    promotedBulkActions={promotedBulkActions}
                    onSelectionChange={handleSelectionChange}
                    headings={PRICE_UPDATE_LOG_HEADINGS}
                    loading={loading}
                    pagination={{
                        hasNext: pagination.currentPage < pagination.totalPages,
                        onNext: handleNext,
                        label: `${(pagination.currentPage - 1) * pagination.perPage + 1}–${Math.min(
                            pagination.currentPage * pagination.perPage,
                            pagination.totalCount
                        )} of ${pagination.totalCount} price update logs`,
                        hasPrevious: pagination.currentPage > 1,
                        onPrevious: handlePrevious,
                    }}
                >
                    {rowMarkup}
                </IndexTable>
            </LegacyCard>
            <ModalComponent
                open={isModalOpen}
                titleHidden={false}
                title="Delete Price Update Log"
                primaryAction={{
                    content: 'Delete',
                    destructive: true,
                    loading: isDeleteLoading,
                    onAction: () => handleDelete(selectedResources)
                }}
                secondaryActions={[]}
                handleModalClose={handleModalOpen}
            >
                <Text as='p'>
                    The log{selectedResources.length > 1 ? 's you selected have' : ' you selected has'} been marked for deletion. Are you sure you want to proceed?
                </Text>
            </ModalComponent>
        </>
    );
};

export default PriceUpdateLogTable;